import { Component, Inject, OnDestroy, OnInit } from '@angular/core';

import { CommonModule, DOCUMENT } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { CalAngularService } from '@cvx/cal-angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OKTA_AUTH } from '@okta/okta-angular';
import { Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { RolesService } from '../../shared/services/roles.service';
import { SidebarService } from '../sidebar/sidebar.service';

export type SiteModule = 'sdd' | 'cdd' | 'bq' | 'sp' | 'ciw';

// The first path segment of each module's routes (app.routes.ts). Matched as a whole segment, not a
// substring, so an id deeper in the URL (a case GUID can contain "cdd") never picks a module.
const MODULE_BY_ROUTE: Record<string, SiteModule> = { bq: 'bq', cdd: 'cdd', sdd: 'sdd', sp: 'sp', investigations: 'ciw' };

const BROWSER_TITLE_BY_MODULE: Record<SiteModule, string> = {
  bq: 'TCO Business Questionnaire',
  cdd: 'TCO Customer Due Diligence',
  sdd: 'TCO Supplier Due Diligence',
  sp: 'TCO Sanctions Screening',
  ciw: 'TCO Compliance Prescreening Workbench'
};

const DEFAULT_BROWSER_TITLE = 'TCO Compliance Hub';

export function moduleForUrl(url: string): SiteModule | undefined {
  const firstSegment = url.split(/[?#]/)[0].split('/').find(segment => segment.length > 0);
  return firstSegment ? MODULE_BY_ROUTE[firstSegment.toLowerCase()] : undefined;
}

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatMenuModule, MatDividerModule, TranslateModule],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss'
})
export class HeaderComponent implements OnInit, OnDestroy {
  languageList = [
    { code: 'en', label: 'Eng' },
    { code: 'ru', label: 'Рус' }
  ];
  siteLanguage: string = 'Eng';
  activeModule: SiteModule = 'sdd';
  userName: string = '';
  userEmail: string = '';
  userRoles: string[] = [];
  private oktaAuthStateSubscription: any;
  private routerEventsSubscription?: Subscription;

  constructor(private sidebar: SidebarService,
    private translate: TranslateService,
    @Inject(DOCUMENT) private document: Document,
    private router: Router,
    private titleService: Title,
    private calService: CalAngularService,
    private rolesService: RolesService,
    @Inject(OKTA_AUTH) private oktaAuth: any) {
    this.translate.addLangs(['en', 'ru']);
    if (localStorage.getItem('language')) {
      const localeCode = localStorage.getItem('language');
      const selectedLanguage = this.languageList.find((language: any) => language.code === localeCode)?.label;
      if (localeCode && selectedLanguage) {
        this.translate.use(localeCode);
        this.translate.setDefaultLang(localeCode);
        this.document.documentElement.lang = localeCode;
        this.siteLanguage = selectedLanguage;
      }
    } else {
      this.translate.setDefaultLang('en');
      this.translate.use('en');
      this.changeSiteLanguage('en');
      this.siteLanguage = 'Eng';
    }
  }

  ngOnInit(): void {
    this.loadUserProfile();

    // Set initial activeModule based on current URL to avoid flash of wrong module
    this.showModuleFor(this.router.url);

    this.routerEventsSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.showModuleFor(event.urlAfterRedirects);
      }
    });
  }

  // A URL outside every module (the home page, request-access) keeps the module that was showing.
  private showModuleFor(url: string): void {
    const module = moduleForUrl(url);
    if (module) {
      this.activeModule = module;
    }
    this.titleService.setTitle(module ? BROWSER_TITLE_BY_MODULE[module] : DEFAULT_BROWSER_TITLE);
  }

  private async loadUserProfile() {
    const idp = localStorage.getItem('bq-idp');
    if (idp === 'okta') {
      // Subscribe to auth state changes to update profile when available
      this.oktaAuthStateSubscription = this.oktaAuth.authStateManager.subscribe(async (authState: any) => {
        if (authState?.isAuthenticated) {
          try {
            const user = await this.oktaAuth.getUser();
            // Fallback: show email or 'User' if name is empty
            this.userName = user?.name || user?.email || 'User';
            this.userEmail = user?.email ?? '';
            this.userRoles = ['External User'];
          } catch (e) {
            console.warn('Failed to get Okta user profile:', e);
          }
        }
      });

      // Also try to load immediately in case user is already authenticated
      try {
        const user = await this.oktaAuth.getUser();
        if (user) {
          this.userName = user?.name ?? '';
          this.userEmail = user?.email ?? '';
          this.userRoles = ['External User'];
        }
      } catch { }
    } else {
      this.calService.isUserSignedIn().subscribe(isSignedIn => {
        if (isSignedIn) {
          const profile = this.calService.cvxClaimsPrincipal;
          this.userName = profile?.name ?? '';
          this.userEmail = profile?.email ?? '';
          // Map roles and remove duplicates
          const mappedRoles = (profile?.roles ?? []).map((r: string) =>
            r.replace(/-Dev$/, '').replace(/-Test$/, '')
          );
          this.userRoles = Array.from(new Set(mappedRoles));
        }
      });
    }
  }

  toggle() {
    this.sidebar.menu.next(true);
  }

  public changeSiteLanguage(localeCode: string): void {
    const selectedLanguage = this.languageList.find((language: any) => language.code === localeCode)?.label.toString();
    if (selectedLanguage) {
      this.siteLanguage = selectedLanguage;
      this.translate.use(localeCode);
      this.translate.setDefaultLang(localeCode);
      this.document.documentElement.lang = localeCode;
      localStorage.setItem("language", localeCode);
    }
  }

  onUserGuideLink() {
    if (this.activeModule === 'sdd') {
      window.open(environment.sddUserGuide, "_blank");
    } else if (this.activeModule === 'bq') {
      this.router.navigate(['/bq/guide']);
    }
  }

  async signOut() {
    const idp = localStorage.getItem('bq-idp');

    if (this.activeModule === 'bq') {
      localStorage.removeItem('bq-idp');
      if (idp === 'okta') {
        await this.oktaAuth.signOut({ clearTokensBeforeRedirect: true });
      } else {
        sessionStorage.setItem('postSignOutRedirect', '/bq/auth-select');
        this.calService.userInitiatedSignOut().subscribe();
      }
    } else {
      this.calService.userInitiatedSignOut().subscribe();
    }
  }

  ngOnDestroy(): void {
    this.routerEventsSubscription?.unsubscribe();

    // Clean up Okta auth state subscription
    if (this.oktaAuthStateSubscription) {
      this.oktaAuthStateSubscription.unsubscribe();
    }
  }
}
